import React from 'react';
import {
  Modal,
  Typography,
  Tag,
  Button,
  Descriptions,
  Row,
  Col,
  Divider,
  Card,
} from 'antd';
import { BankOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface CoverageItem {
  name: string;
  description?: string;
  type: string;
  coverage_amount?: number;
  copayment_amount?: number;
  coverage_times_per_month?: number;
}

interface InsurancePlan {
  id: string;
  name: string;
  payor: string;
  description: string;
  planDetails?: {
    free_consultations?: number;
    free_consultations_per_year?: number;
    free_consultations_per_month?: number;
    free_consultations_per_week?: number;
    coverage?: CoverageItem[];
    copayment_amount?: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface PlanDetailDialogProps {
  visible: boolean;
  plan: InsurancePlan | null;
  onClose: () => void;
}

const formatPayorName = (payorName: string) => {
  return payorName
    .split('')
    .map((char, index) => {
      if (index === 0) return char.toUpperCase();
      if (char === char.toUpperCase() || payorName[index - 1] === ' ') {
        return char;
      }
      return char;
    })
    .join('')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (str) => str.toUpperCase());
};

const PlanDetailDialog: React.FC<PlanDetailDialogProps> = ({
  visible,
  plan,
  onClose,
}) => {
  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <BankOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          Plan Details
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>
      ]}
      width={700}
      destroyOnClose
    >
      {plan && (
        <div style={{ padding: '16px 0' }}>
          <Row gutter={[24, 16]}>
            <Col span={24}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <BankOutlined
                  style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }}
                />
                <div style={{ marginTop: 16 }}>
                  <Title level={3} style={{ margin: 0 }}>
                    {plan.name}
                  </Title>
                  <Text type="secondary">ID: {plan.id}</Text>
                </div>
              </div>
            </Col>
          </Row>

          <Divider />

          <Descriptions bordered column={1}>
            <Descriptions.Item label="Name">
              {plan.name}
            </Descriptions.Item>
            <Descriptions.Item label="Payor">
              <Tag color="blue">{formatPayorName(plan.payor)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              {plan.description || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {dayjs(plan.createdAt).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="Updated At">
              {dayjs(plan.updatedAt).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
          </Descriptions>

          {plan.planDetails && (
            <>
              <Divider orientation="left">Plan Details</Divider>
              <Descriptions bordered column={1}>
                {plan.planDetails.free_consultations && (
                  <Descriptions.Item label="Free Consultations">
                    {plan.planDetails.free_consultations}
                  </Descriptions.Item>
                )}
                {plan.planDetails.free_consultations_per_year && (
                  <Descriptions.Item label="Free Consultations Per Year">
                    {plan.planDetails.free_consultations_per_year}
                  </Descriptions.Item>
                )}
                {plan.planDetails.free_consultations_per_month && (
                  <Descriptions.Item label="Free Consultations Per Month">
                    {plan.planDetails.free_consultations_per_month}
                  </Descriptions.Item>
                )}
                {plan.planDetails.free_consultations_per_week && (
                  <Descriptions.Item label="Free Consultations Per Week">
                    {plan.planDetails.free_consultations_per_week}
                  </Descriptions.Item>
                )}
                {plan.planDetails.copayment_amount !== undefined && (
                  <Descriptions.Item label="Copayment Amount">
                      {plan.planDetails.copayment_amount}
                  </Descriptions.Item>
                )}
              </Descriptions>

              {plan.planDetails.coverage && plan.planDetails.coverage.length > 0 && (
                <>
                  <Divider orientation="left">Coverage Items</Divider>
                  {plan.planDetails.coverage.map((coverageItem, index) => (
                    <Card
                      key={index}
                      size="small"
                      style={{ marginBottom: 16 }}
                      title={
                        <div>
                          <Text strong>{coverageItem.name}</Text>
                          <Tag color="blue" style={{ marginLeft: 8 }}>
                            {coverageItem.type}
                          </Tag>
                        </div>
                      }
                    >
                      <Descriptions bordered column={1} size="small">
                        {coverageItem.description && (
                          <Descriptions.Item label="Description">
                            {coverageItem.description}
                          </Descriptions.Item>
                        )}
                        {coverageItem.coverage_amount !== undefined && coverageItem.coverage_amount !== null && (
                          <Descriptions.Item label="Coverage Amount">
                            <Text strong>
                              ${typeof coverageItem.coverage_amount === 'number' 
                                ? coverageItem.coverage_amount.toFixed(2) 
                                : parseFloat(String(coverageItem.coverage_amount)).toFixed(2)}
                            </Text>
                          </Descriptions.Item>
                        )}
                        {coverageItem.copayment_amount !== undefined && coverageItem.copayment_amount !== null && (
                          <Descriptions.Item label="Copay">
                            <Text strong>
                              ${typeof coverageItem.copayment_amount === 'number' 
                                ? coverageItem.copayment_amount.toFixed(2) 
                                : parseFloat(String(coverageItem.copayment_amount)).toFixed(2)}
                            </Text>
                          </Descriptions.Item>
                        )}
                        {coverageItem.coverage_times_per_month !== undefined && coverageItem.coverage_times_per_month !== null && (
                          <Descriptions.Item label="Coverage Times Per Month">
                            <Text strong>{coverageItem.coverage_times_per_month}</Text>
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                    </Card>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default PlanDetailDialog;

